import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { QairoLogo } from "../components/QairoLogo";
import { formatApiError } from "../lib/formatApiError";
import { API } from "../lib/constants";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref");
  const source = searchParams.get("src") || "direct";

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  useEffect(() => {
    if (referralCode) {
      axios.post(`${API}/affiliate/track-click`, {
        affiliate_code: referralCode,
        source: source
      }).catch(() => {});
    }
  }, [referralCode, source]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name, referralCode);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-black">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between">
          <Link to="/"><QairoLogo variant="light" /></Link>
          <Link to="/login" className="text-sm font-medium hover:text-[#0066FF]">Login</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="border border-black p-8 rounded-xl">
            <h1 className="text-2xl font-bold mb-6">Create your account</h1>
            <p className="text-gray-600 mb-6">Start with 10 free credits</p>
            {error && <div className="bg-red-50 border border-red-500 text-red-700 p-3 mb-4 text-sm" data-testid="register-error">{error}</div>}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required data-testid="register-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required data-testid="register-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold block mb-2">Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full border border-black p-3 focus:ring-2 focus:ring-[#0066FF] outline-none rounded-lg" required minLength={6} data-testid="register-password" />
              </div>
              <button type="submit" disabled={loading} className="w-full bg-[#0066FF] text-white py-3 font-bold hover:bg-[#0052CC] transition-colors disabled:opacity-50 rounded-lg" data-testid="register-submit">
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>
            <p className="text-center mt-6 text-sm">
              Already have an account? <Link to="/login" className="text-[#0066FF] font-bold">Login</Link>
            </p>
          </div>
        </div>
      </main>

      <footer className="py-8 px-8 border-t border-black bg-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <QairoLogo variant="dark" />
          <p className="text-sm text-gray-400">&copy; 2026 QAIRO AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
